import styled from 'styled-components';
import getThemeByProps from '@userzoomlibraries/toolkit-theme-manager/get-theme-by-props';
import React, { Fragment, useRef, useContext, useMemo, useCallback, useState, useEffect, useImperativeHandle } from 'react';
import PropTypes from '@userzoomlibraries/toolkit-js-utils/prop-types';
import noop from '@userzoomlibraries/toolkit-js-utils/noop';
import getSafeHTMLElement from '@userzoomlibraries/toolkit-js-utils/get-safe-html-element';
import useAriaAttributes from '@userzoomlibraries/toolkit-react-utils/use-aria-attributes';
import useDataAttributes from '@userzoomlibraries/toolkit-react-utils/use-data-attributes';
import Tag from '@userzoomlibraries/toolkit-tag';
import { jsx, jsxs } from 'react/jsx-runtime';
import LinkedFloatingElement from '@userzoomlibraries/toolkit-floating-element/linked-floating-element';
import isFunction from '@userzoomlibraries/toolkit-js-utils/is-function';
import useFocusable, { focusablePropTypes, focusableDefaultProps } from '@userzoomlibraries/toolkit-react-utils/use-focusable';
import useComparePrevious from '@userzoomlibraries/toolkit-react-utils/use-compare-previous';
import isDefinedAndNotNull from '@userzoomlibraries/toolkit-js-utils/is-defined-and-not-null';
import usePrevious from '@userzoomlibraries/toolkit-react-utils/use-previous';
import Icon from '@userzoomlibraries/toolkit-icon';
import { a as angleDown, b as angleUp, c as close } from './index.js';
import InputText from '@userzoomlibraries/toolkit-input/text';
import { S as SelectOptionSeparator } from './index.styled2.js';

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */

var DEFAULT_VALUE_VIEWER_FORMATTER = function DEFAULT_VALUE_VIEWER_FORMATTER(_ref) {
  var selectedOptions = _ref.selectedOptions,
      isSelectDisabled = _ref.isSelectDisabled,
      isSelectMultiple = _ref.isSelectMultiple,
      deselectOption = _ref.deselectOption;
  var items = selectedOptions.map(function (each) {
    var content = each.label({
      id: each.id,
      value: each.value,
      isSelected: selectedOptions.some(function (selectedOption) {
        return selectedOption.value === each.value;
      }),
      isDisabled: each.disabled
    });
    return {
      id: each.id,
      content: content
    };
  });

  if (!isSelectMultiple) {
    items = items.map(function (each) {
      return /*#__PURE__*/jsx(Fragment, {
        children: each.content
      }, each.id);
    });
  } else {
    items = items.map(function (each) {
      return /*#__PURE__*/jsx(Tag, {
        disabled: isSelectDisabled,
        closable: true,
        onClose: function onClose() {
          return deselectOption(each.id);
        },
        children: each.content
      }, each.id);
    });
  }

  return items;
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var DEFAULT_SEARCH_MATCHER = function DEFAULT_SEARCH_MATCHER(_ref) {
  var _ref$filterTerm = _ref.filterTerm,
      filterTerm = _ref$filterTerm === void 0 ? '' : _ref$filterTerm,
      searchTerm = _ref.searchTerm,
      _ref$searchBy = _ref.searchBy,
      searchBy = _ref$searchBy === void 0 ? '' : _ref$searchBy,
      value = _ref.value,
      _ref$dataAttributes = _ref.dataAttributes,
      dataAttributes = _ref$dataAttributes === void 0 ? {} : _ref$dataAttributes;
  var searchByToProcess = Array.isArray(searchBy) ? searchBy : [searchBy];
  var purgedFilteringTerm = filterTerm.trim().toLowerCase();
  var isVisible = searchByToProcess.reduce(function (acc, current) {
    // Check the value
    if (current === 'value') {
      var valueToString = value.toString().toLowerCase();
      var matchWithValue = valueToString.includes(purgedFilteringTerm);
      return acc || matchWithValue;
    } // Check the searchTerm


    if (current === 'searchTerm' && searchTerm) {
      var searchTermToString = searchTerm.toString().toLowerCase();
      var matchWithSearchTerm = searchTermToString.includes(purgedFilteringTerm);
      return acc || matchWithSearchTerm;
    } // Check the data attributes


    if (current.match(/^data-/gi)) {
      var dataAttributeToProcess = dataAttributes[current];

      if (dataAttributeToProcess) {
        var dataAttributeToString = dataAttributeToProcess.toString().toLowerCase();
        var matchWithDataAttribute = dataAttributeToString.includes(purgedFilteringTerm);
        return acc || matchWithDataAttribute;
      }
    }

    return acc;
  }, false);
  return isVisible;
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var DIRECTIONS = Object.freeze({
  DOWN: 'down',
  UP: 'up',
  OVER: 'over'
});

function _toConsumableArray$4(arr) { return _arrayWithoutHoles$4(arr) || _iterableToArray$4(arr) || _unsupportedIterableToArray$a(arr) || _nonIterableSpread$4(); }

function _nonIterableSpread$4() { throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray$a(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray$a(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$a(o, minLen); }

function _iterableToArray$4(iter) { if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter); }

function _arrayWithoutHoles$4(arr) { if (Array.isArray(arr)) return _arrayLikeToArray$a(arr); }

function _arrayLikeToArray$a(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

var range = function range(min, max) {
  return _toConsumableArray$4(Array(max - min + 1).keys()).map(function (i) {
    return i + min;
  });
};

var generateId = function generateId() {
  var characters = [].concat(_toConsumableArray$4(range(0, 9).map(function (each) {
    return "".concat(each);
  })), _toConsumableArray$4(range(65, 90).map(function (each) {
    return String.fromCharCode(each);
  })), _toConsumableArray$4(range(97, 122).map(function (each) {
    return String.fromCharCode(each);
  })));
  var shuffledArray = characters.sort(function () {
    return 0.5 - Math.random();
  });
  return shuffledArray.slice(0, 8).join('');
};

var useId = function useId(paramId) {
  var id = useRef(paramId || generateId());
  return id.current;
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var SelectPropsContext = /*#__PURE__*/React.createContext({});

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var StyledLinkedFloatingElement = styled(LinkedFloatingElement).withConfig({
  componentId: "gz-select-1675939618424__sc-b31nmd-0"
})(["border:none;box-sizing:border-box;padding:0;pointer-events:all;white-space:normal;"]);

function _toConsumableArray$3(arr) { return _arrayWithoutHoles$3(arr) || _iterableToArray$3(arr) || _unsupportedIterableToArray$9(arr) || _nonIterableSpread$3(); }

function _nonIterableSpread$3() { throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray$9(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray$9(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$9(o, minLen); }

function _iterableToArray$3(iter) { if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter); }

function _arrayWithoutHoles$3(arr) { if (Array.isArray(arr)) return _arrayLikeToArray$9(arr); }

function _arrayLikeToArray$9(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function ownKeys$5(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$5(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$5(Object(source), true).forEach(function (key) { _defineProperty$5(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$5(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$5(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var useSelect = function useSelect() {
  var _useContext = useContext(SelectPropsContext),
      autoFocus = _useContext.autoFocus,
      appendTo = _useContext.appendTo,
      disabled = _useContext.disabled,
      direction = _useContext.direction,
      disableBackgroundScroll = _useContext.disableBackgroundScroll,
      avoidReposition = _useContext.avoidReposition,
      avoidHideOptionsOnSearch = _useContext.avoidHideOptionsOnSearch,
      forceOpenEvenNotFit = _useContext.forceOpenEvenNotFit,
      forceDefaultCloseMethod = _useContext.forceDefaultCloseMethod,
      defaultValue = _useContext.defaultValue,
      hideSelected = _useContext.hideSelected,
      multiple = _useContext.multiple,
      value = _useContext.value,
      invalid = _useContext.invalid,
      clearable = _useContext.clearable,
      searchable = _useContext.searchable,
      searchBy = _useContext.searchBy,
      placeholder = _useContext.placeholder,
      name = _useContext.name,
      closeOnSelect = _useContext.closeOnSelect,
      messageOnNoResults = _useContext.messageOnNoResults,
      searchMatcher = _useContext.searchMatcher,
      prefix = _useContext.prefix,
      arrowRenderer = _useContext.arrowRenderer,
      valueViewerFormatter = _useContext.valueViewerFormatter,
      onFocus = _useContext.onFocus,
      onBlur = _useContext.onBlur,
      onChange = _useContext.onChange,
      onOpened = _useContext.onOpened,
      onClosed = _useContext.onClosed,
      id = _useContext.id,
      selectTriggerRef = _useContext.selectTriggerRef,
      selectListRef = _useContext.selectListRef,
      selectInputRef = _useContext.selectInputRef,
      isFocused = _useContext.isFocused,
      setIsFocused = _useContext.setIsFocused,
      isOpened = _useContext.isOpened,
      setIsOpened = _useContext.setIsOpened,
      selectOptions = _useContext.selectOptions,
      selectedOptions = _useContext.selectedOptions,
      activeOptionId = _useContext.activeOptionId,
      filteringTerm = _useContext.filteringTerm,
      setSelectInfo = _useContext.setSelectInfo;

  var selectCloseOnSelect = useMemo(function () {
    if (isDefinedAndNotNull(closeOnSelect)) {
      return closeOnSelect;
    }

    return !multiple;
  }, [closeOnSelect, multiple]);
  var selectDirection = useMemo(function () {
    return direction === DIRECTIONS.DOWN ? LinkedFloatingElement.PLACEMENTS.BOTTOM : LinkedFloatingElement.PLACEMENTS.TOP;
  }, [direction]);
  var isSelectClearable = useMemo(function () {
    // By default, all searchable selects or multiple selects are clearable.
    if (!isDefinedAndNotNull(clearable)) {
      return searchable || multiple;
    }

    return clearable;
  }, [clearable, searchable, multiple]);
  var isControlledSelect = useMemo(function () {
    if (isDefinedAndNotNull(defaultValue) && !isDefinedAndNotNull(value)) {
      return false;
    }

    if (!isDefinedAndNotNull(defaultValue) && isDefinedAndNotNull(value)) {
      return true;
    }

    if (!isDefinedAndNotNull(defaultValue) && !isDefinedAndNotNull(value)) {
      return false;
    }

    console.error("Warning: \"Gearz.Select\" contains a select with both value and defaultValue props.\n      Select elements must be either controlled or uncontrolled (specify either the value prop, or the defaultValue prop, but not both).\n      Decide between using a controlled or uncontrolled select element and remove one of these props. More info: https://reactjs.org/link/controlled-components%s");
    return false;
  }, [useComparePrevious(value), useComparePrevious(defaultValue)]);
  var activeOption = useCallback(function (optionId) {
    setSelectInfo(function (prevState) {
      return _objectSpread$5(_objectSpread$5({}, prevState), {}, {
        activeOptionId: optionId
      });
    });
  }, [setSelectInfo]);
  var openSelect = useCallback(function () {
    if (!disabled) {
      setIsOpened(true);
    }
  }, [disabled, setIsOpened]);
  var closeSelect = useCallback(function () {
    if (!disabled) {
      setIsOpened(false);
      activeOption(null);
    }
  }, [disabled, setIsOpened, activeOption]);
  var closeSelectIfNeeded = useCallback(function () {
    if (selectCloseOnSelect) {
      closeSelect();
    }
  }, [selectCloseOnSelect, closeSelect]);
  var onFocusContainerHandler = useCallback(function (event) {
    // If the relatedTarget is not insde the select trigger, we come from the outside, so we call onFocus
    if (selectTriggerRef.current && !selectTriggerRef.current.contains(event.relatedTarget)) {
      // In case of the select is not opened or if is opened and the relatedTarget is not inside the select list, we come from the outside, so we call onFocus
      if (!isOpened || isOpened && selectListRef.current && !selectListRef.current.contains(event.relatedTarget)) {
        setIsFocused(true);
        onFocus(event); // In case of searchable select, we have to focus the select input

        if (searchable && selectInputRef.current) {
          selectInputRef.current.focus();
        }
      }
    }
  }, [isOpened, selectInputRef, searchable, onFocus, selectTriggerRef, selectListRef, setIsFocused]);
  var onBlurContainerHandler = useCallback(function (event) {
    // If the relatedTarget is not insde the select trigger, we are outside, so we call onBlue
    if (selectTriggerRef.current && !selectTriggerRef.current.contains(event.relatedTarget)) {
      // In case of the select is not opened or if is opened and the relatedTarget is not inside the select list, we are outside, so we call onBlur
      if (!isOpened || isOpened && selectListRef.current && !selectListRef.current.contains(event.relatedTarget)) {
        setIsFocused(false);
        onBlur(event); // In case of multiple searchable select, we have to reset the size of the select input

        if (multiple && searchable && selectInputRef.current) {
          selectInputRef.current.style.width = '100%';

          if (selectedOptions.length) {
            selectInputRef.current.parentNode.style.width = '1ch';
          }
        }
      }
    }
  }, [multiple, searchable, isOpened, selectListRef, selectTriggerRef, onBlur, selectInputRef, setIsFocused, selectedOptions]);
  var selectOption = useCallback(function (optionInfo) {
    if (multiple) {
      setSelectInfo(function (prevState) {
        if (!prevState.selectedOptions.some(function (each) {
          return each.id === optionInfo.id;
        })) {
          return _objectSpread$5(_objectSpread$5({}, prevState), {}, {
            selectedOptions: [].concat(_toConsumableArray$3(prevState.selectedOptions), [optionInfo])
          });
        }

        return prevState;
      });
    } else {
      setSelectInfo(function (prevState) {
        return _objectSpread$5(_objectSpread$5({}, prevState), {}, {
          selectedOptions: [optionInfo]
        });
      });
    }
  }, [multiple, setSelectInfo]);
  var deselectOption = useCallback(function (optionId) {
    setSelectInfo(function (prevState) {
      var newValues = prevState.selectedOptions.filter(function (each) {
        return each.id !== optionId;
      });
      return _objectSpread$5(_objectSpread$5({}, prevState), {}, {
        selectedOptions: newValues
      });
    }); // We need to re-focus the select input of the focused select

    if (isFocused) {
      if (searchable && selectInputRef.current) {
        selectInputRef.current.focus();
      } else if (!searchable && selectTriggerRef.current) {
        selectTriggerRef.current.focus();
      }
    }
  }, [isFocused, setSelectInfo, searchable, selectTriggerRef, selectInputRef]);
  var deselectOptionByValue = useCallback(function (optionValue) {
    setSelectInfo(function (prevState) {
      var newValues = prevState.selectedOptions.filter(function (each) {
        return each.value !== optionValue;
      });
      return _objectSpread$5(_objectSpread$5({}, prevState), {}, {
        selectedOptions: newValues
      });
    }); // We need to re-focus the select input of the focused select

    if (isFocused) {
      if (searchable && selectInputRef.current) {
        selectInputRef.current.focus();
      } else if (!searchable && selectTriggerRef.current) {
        selectTriggerRef.current.focus();
      }
    }
  }, [isFocused, setSelectInfo, searchable, selectTriggerRef, selectInputRef]);
  var deselectAllOptions = useCallback(function () {
    setSelectInfo(function (prevState) {
      return _objectSpread$5(_objectSpread$5({}, prevState), {}, {
        selectedOptions: []
      });
    }); // We need to re-focus the select input of the focused select

    if (isFocused) {
      if (searchable && selectInputRef.current) {
        selectInputRef.current.focus();
      } else if (!searchable && selectTriggerRef.current) {
        selectTriggerRef.current.focus();
      }
    }
  }, [isFocused, searchable, selectTriggerRef, selectInputRef, setSelectInfo]);
  var updateOption = useCallback(function (optionId, optionInfo) {
    setSelectInfo(function (prevState) {
      return _objectSpread$5(_objectSpread$5({}, prevState), {}, {
        selectOptions: prevState.selectOptions.map(function (eachOption) {
          if (eachOption.id === optionId) {
            var newOptionInfo = _objectSpread$5(_objectSpread$5({}, eachOption), optionInfo);

            return newOptionInfo;
          }

          return eachOption;
        })
      });
    });
  }, [setSelectInfo]);
  var registerOption = useCallback(function (optionInfo) {
    setSelectInfo(function (prevState) {
      return _objectSpread$5(_objectSpread$5({}, prevState), {}, {
        selectOptions: [].concat(_toConsumableArray$3(prevState.selectOptions), [optionInfo])
      });
    });
  }, [setSelectInfo]);
  var unregisterOption = useCallback(function (optionId) {
    setSelectInfo(function (prevState) {
      return _objectSpread$5(_objectSpread$5({}, prevState), {}, {
        selectOptions: prevState.selectOptions.filter(function (each) {
          return each.id !== optionId;
        })
      });
    });
  }, [setSelectInfo]);
  var placeholderToUse = useCallback(function () {
    var placeholderAsFunction = isFunction(placeholder) ? placeholder : function () {
      return placeholder;
    };
    return placeholderAsFunction({
      selectedOptions: selectedOptions
    });
  }, [placeholder, selectedOptions]);
  var prefixToUse = useCallback(function () {
    var prefixAsFunction = isFunction(prefix) ? prefix : function () {
      return prefix;
    };
    return prefixAsFunction({
      selectedOptions: selectedOptions,
      disabled: disabled,
      multiple: multiple,
      invalid: invalid,
      isOpened: isOpened,
      isFocused: isFocused,
      searchable: searchable
    });
  }, [prefix, selectedOptions, disabled, multiple, invalid, isOpened, isFocused, searchable]);
  var setFilterTerm = useCallback(function (term) {
    setSelectInfo(function (prevState) {
      return _objectSpread$5(_objectSpread$5({}, prevState), {}, {
        filteringTerm: term
      });
    });
  }, [setSelectInfo]);
  var checkVisibilityByTerm = useCallback(function (_ref) {
    var _ref$filterTerm = _ref.filterTerm,
        filterTerm = _ref$filterTerm === void 0 ? '' : _ref$filterTerm,
        searchTerm = _ref.searchTerm,
        valueToCheck = _ref.value,
        _ref$dataAttributes = _ref.dataAttributes,
        dataAttributes = _ref$dataAttributes === void 0 ? {} : _ref$dataAttributes,
        isSelected = _ref.isSelected,
        hideSelectedToCheck = _ref.hideSelected;

    if (isSelected && hideSelectedToCheck) {
      // In case that the option is selected and we have the flag hideSelected activated, we have to hide the option
      return false;
    }

    return searchMatcher({
      filterTerm: filterTerm,
      searchTerm: searchTerm,
      searchBy: searchBy,
      value: valueToCheck,
      dataAttributes: dataAttributes
    });
  }, [searchBy, searchMatcher]);
  var searchByTerm = useCallback(function () {
    var filterTerm = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';

    if (selectOptions.length) {
      var auxSelectOptions = selectOptions.map(function (each) {
        var visibilityByTerm = checkVisibilityByTerm({
          filterTerm: filterTerm,
          searchTerm: each.searchTerm,
          value: each.value,
          dataAttributes: each.dataAttributes
        });
        updateOption(each.id, {
          isVisible: avoidHideOptionsOnSearch ? true : visibilityByTerm
        });
        return _objectSpread$5(_objectSpread$5({}, each), {}, {
          isVisible: visibilityByTerm
        });
      }); // Active the first option that match with the filter term

      var firstVisibleOption = auxSelectOptions.find(function (each) {
        return each.isVisible;
      });

      if (firstVisibleOption) {
        var firstVisibleId = firstVisibleOption.id;
        activeOption(firstVisibleId); // We have to scroll the list element to the new active element

        var optionElement = document.getElementById(firstVisibleId);

        if (optionElement) {
          optionElement.scrollIntoView({
            block: 'center'
          });
        }
      } else {
        activeOption(null);
      }
    }
  }, [selectOptions, avoidHideOptionsOnSearch, checkVisibilityByTerm, updateOption, activeOption]);
  return {
    selectId: id,
    selectTriggerId: "".concat(id, "_trigger"),
    selectTriggerRef: selectTriggerRef,
    selectListId: "".concat(id, "_list"),
    selectListRef: selectListRef,
    selectInputId: "".concat(id, "_input"),
    selectInputRef: selectInputRef,
    selectAppendTo: appendTo,
    isSelectOpened: isOpened,
    isSelectFocused: isFocused,
    isSelectDisabled: disabled,
    isSelectClearable: isSelectClearable,
    isSelectSearchable: searchable,
    isSelectMultiple: multiple,
    isSelectInvalid: invalid,
    closeOnSelect: selectCloseOnSelect,
    selectAutoFocus: autoFocus,
    selectHideSelected: hideSelected,
    isControlledSelect: isControlledSelect,
    selectDirection: selectDirection,
    selectName: name,
    selectValue: value,
    selectDefaultValue: defaultValue,
    selectPrefix: prefixToUse(),
    arrowRenderer: arrowRenderer,
    messageOnNoResults: messageOnNoResults,
    disableBackgroundScroll: disableBackgroundScroll,
    avoidReposition: avoidReposition,
    avoidHideOptionsOnSearch: avoidHideOptionsOnSearch,
    forceOpenEvenNotFit: forceOpenEvenNotFit,
    forceDefaultCloseMethod: forceDefaultCloseMethod,
    valueViewerFormatter: valueViewerFormatter,
    selectPlaceholder: placeholderToUse(),
    selectOptions: selectOptions,
    selectedOptions: selectedOptions,
    activeOptionId: activeOptionId,
    filteringTerm: filteringTerm,
    openSelect: openSelect,
    closeSelect: closeSelect,
    closeSelectIfNeeded: closeSelectIfNeeded,
    selectOption: selectOption,
    deselectOption: deselectOption,
    deselectOptionByValue: deselectOptionByValue,
    deselectAllOptions: deselectAllOptions,
    activeOption: activeOption,
    updateOption: updateOption,
    registerOption: registerOption,
    unregisterOption: unregisterOption,
    searchByTerm: searchByTerm,
    setFilterTerm: setFilterTerm,
    onFocusContainerHandler: onFocusContainerHandler,
    onBlurContainerHandler: onBlurContainerHandler,
    checkVisibilityByTerm: checkVisibilityByTerm,
    onChange: onChange,
    onOpened: onOpened,
    onClosed: onClosed
  };
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var SelectOptionGroupContext = /*#__PURE__*/React.createContext();

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */

var useOptionGroup = function useOptionGroup() {
  return useContext(SelectOptionGroupContext) || {};
};

var _excluded$3 = ["id", "className", "value", "children", "disabled", "searchTerm"];

function _toConsumableArray$2(arr) { return _arrayWithoutHoles$2(arr) || _iterableToArray$2(arr) || _unsupportedIterableToArray$8(arr) || _nonIterableSpread$2(); }

function _nonIterableSpread$2() { throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _iterableToArray$2(iter) { if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter); }

function _arrayWithoutHoles$2(arr) { if (Array.isArray(arr)) return _arrayLikeToArray$8(arr); }

function ownKeys$4(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$4(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$4(Object(source), true).forEach(function (key) { _defineProperty$4(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$4(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$4(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _slicedToArray$7(arr, i) { return _arrayWithHoles$7(arr) || _iterableToArrayLimit$7(arr, i) || _unsupportedIterableToArray$8(arr, i) || _nonIterableRest$7(); }

function _nonIterableRest$7() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray$8(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray$8(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$8(o, minLen); }

function _arrayLikeToArray$8(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit$7(arr, i) { var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]; if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles$7(arr) { if (Array.isArray(arr)) return arr; }

function _objectWithoutProperties$3(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose$3(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose$3(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
var SelectOption$1 = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var id = _ref.id,
      className = _ref.className,
      value = _ref.value,
      children = _ref.children,
      disabled = _ref.disabled,
      searchTerm = _ref.searchTerm,
      otherProps = _objectWithoutProperties$3(_ref, _excluded$3);

  var ariaAttributes = useAriaAttributes(otherProps);
  var dataAttributes = useDataAttributes(otherProps);
  var childrenAsFunction = useMemo(function () {
    return isFunction(children) ? children : function () {
      return children;
    };
  }, [children]);
  var optionRef = useRef();

  var _useSelect = useSelect(),
      selectId = _useSelect.selectId,
      selectTriggerId = _useSelect.selectTriggerId,
      selectOptions = _useSelect.selectOptions,
      selectedOptions = _useSelect.selectedOptions,
      selectHideSelected = _useSelect.selectHideSelected,
      activeOptionId = _useSelect.activeOptionId,
      isControlledSelect = _useSelect.isControlledSelect,
      isSelectSearchable = _useSelect.isSelectSearchable,
      isSelectMultiple = _useSelect.isSelectMultiple,
      filteringTerm = _useSelect.filteringTerm,
      avoidHideOptionsOnSearch = _useSelect.avoidHideOptionsOnSearch,
      setFilterTerm = _useSelect.setFilterTerm,
      selectOption = _useSelect.selectOption,
      deselectOption = _useSelect.deselectOption,
      activeOption = _useSelect.activeOption,
      updateOption = _useSelect.updateOption,
      registerOption = _useSelect.registerOption,
      unregisterOption = _useSelect.unregisterOption,
      checkVisibilityByTerm = _useSelect.checkVisibilityByTerm,
      closeSelectIfNeeded = _useSelect.closeSelectIfNeeded,
      onChange = _useSelect.onChange;

  var optionIdRef = useRef(id || "".concat(selectId, "_").concat(value));

  var _useOptionGroup = useOptionGroup(),
      optionGroupDisabled = _useOptionGroup.disabled;

  var _useState = useState(function () {
    return optionIdRef.current === activeOptionId;
  }),
      _useState2 = _slicedToArray$7(_useState, 2),
      isActive = _useState2[0],
      setIsActive = _useState2[1];

  var focusableProps = useFocusable(optionRef.current, _objectSpread$4(_objectSpread$4({}, otherProps), {}, {
    disabled: disabled || optionGroupDisabled
  }));
  var isDisabled = useMemo(function () {
    return focusableProps['aria-disabled'];
  }, [focusableProps]);
  var isSelected = useMemo(function () {
    return selectedOptions.some(function (each) {
      return each.value === value;
    });
  }, [value, selectedOptions]);
  var isFiltered = useMemo(function () {
    var option = selectOptions.find(function (each) {
      return each.id === optionIdRef.current;
    });

    if (option) {
      return !option.isVisible;
    }

    return false;
  }, [selectOptions]);
  var onClickHandler = useCallback(function () {
    if (!isDisabled) {
      if (!selectedOptions.some(function (each) {
        return each.id === optionIdRef.current;
      })) {
        var newSelectOption = {
          id: optionIdRef.current,
          value: value,
          label: childrenAsFunction
        };

        if (!isControlledSelect) {
          selectOption(newSelectOption);
        }

        var newValue = [];

        if (isSelectMultiple) {
          newValue = [].concat(_toConsumableArray$2(selectedOptions), [newSelectOption]).map(function (each) {
            return each.value;
          });
        } else {
          newValue = newSelectOption.value;
        }

        onChange({
          value: newValue,
          optionSelected: newSelectOption
        });
      } else if (isSelectMultiple) {
        if (!isControlledSelect) {
          deselectOption(optionIdRef.current);
        }

        var optionDeselected = selectedOptions.find(function (each) {
          return each.id === optionIdRef.current;
        });

        var _newValue = selectedOptions.filter(function (each) {
          return each.id !== optionIdRef.current;
        }).map(function (each) {
          return each.value;
        });

        onChange({
          value: _newValue,
          optionDeselected: optionDeselected
        });
      }

      closeSelectIfNeeded();
      activeOption(optionIdRef.current);
      setFilterTerm(''); // We have to re-focus the trigger to manage properly the focus state

      var selectTrigger = document.getElementById(selectTriggerId);

      if (selectTrigger) {
        // In case of searchable select, we have to focus the select trigger input
        if (isSelectSearchable && isSelectMultiple) {
          var input = selectTrigger.querySelector('.gz-select__input .gz-input');

          if (input) {
            input.focus();
          }
        } else {
          selectTrigger.focus();
        }
      }
    }
  }, [isControlledSelect, childrenAsFunction, isSelectMultiple, isSelectSearchable, value, selectTriggerId, selectedOptions, isDisabled, activeOption, selectOption, deselectOption, closeSelectIfNeeded, onChange]);
  var onMouseEnterHandler = useCallback(function () {
    if (!isDisabled) {
      setIsActive(true);
      activeOption(optionIdRef.current);
    }
  }, [isDisabled, activeOption]);
  var onMouseLeaveHandler = useCallback(function () {
    if (!isDisabled) {
      setIsActive(false);
    }
  }, [isDisabled]);
  useEffect(function () {
    var optionId = optionIdRef.current;
    registerOption({
      id: optionId,
      value: value,
      label: childrenAsFunction,
      searchTerm: searchTerm,
      dataAttributes: dataAttributes,
      isVisible: avoidHideOptionsOnSearch ? true : checkVisibilityByTerm({
        filterTerm: filteringTerm,
        searchTerm: searchTerm,
        value: value,
        dataAttributes: dataAttributes,
        isSelected: isSelected,
        hideSelected: selectHideSelected
      })
    });
    return function () {
      unregisterOption(optionId);
    };
  }, [checkVisibilityByTerm, registerOption, unregisterOption]);
  useEffect(function () {
    updateOption(optionIdRef.current, {
      disabled: isDisabled
    });
  }, [isDisabled, updateOption]);
  useEffect(function () {
    updateOption(optionIdRef.current, {
      dataAttributes: dataAttributes
    });
  }, [useComparePrevious(dataAttributes), updateOption]);
  useEffect(function () {
    updateOption(optionIdRef.current, {
      isVisible: avoidHideOptionsOnSearch ? true : checkVisibilityByTerm({
        filterTerm: filteringTerm,
        searchTerm: searchTerm,
        value: value,
        dataAttributes: dataAttributes,
        isSelected: isSelected,
        hideSelected: selectHideSelected
      })
    });
  }, [avoidHideOptionsOnSearch, filteringTerm, isSelected, selectHideSelected, searchTerm, value, useComparePrevious(dataAttributes), checkVisibilityByTerm, updateOption]);
  useEffect(function () {
    if (activeOptionId === optionIdRef.current) {
      setIsActive(true);
    } else {
      setIsActive(false);
    }
  }, [activeOptionId]);
  useImperativeHandle(ref, function () {
    return optionRef.current;
  });
  return (
    /*#__PURE__*/
    // eslint-disable-next-line jsx-a11y/interactive-supports-focus
    jsx("div", _objectSpread$4(_objectSpread$4(_objectSpread$4(_objectSpread$4({
      id: optionIdRef.current,
      ref: optionRef,
      className: "gz-select__option ".concat(className),
      role: "option",
      "aria-selected": isSelected,
      "data-active": isActive,
      "data-value": value,
      "data-visible": !isFiltered,
      onMouseEnter: onMouseEnterHandler,
      onMouseLeave: onMouseLeaveHandler,
      onClick: onClickHandler // eslint-disable-next-line react/jsx-props-no-spreading

    }, ariaAttributes), dataAttributes), focusableProps), {}, {
      children: /*#__PURE__*/jsx("div", {
        className: "gz-select__option-content",
        children: childrenAsFunction({
          id: optionIdRef.current,
          value: value,
          isSelected: isSelected,
          isDisabled: isDisabled,
          selectedOptions: selectedOptions
        })
      })
    }))
  );
});
SelectOption$1.COMPONENT_ID = 'Gearz.Select.Option';
SelectOption$1.displayName = SelectOption$1.COMPONENT_ID;
SelectOption$1.propTypes = _objectSpread$4(_objectSpread$4({}, focusablePropTypes), {}, {
  id: PropTypes.stringWithDescription('Id of the option.'),
  className: PropTypes.stringWithDescription('Custom class to be applied.'),
  children: PropTypes.oneOfTypeWithDescription([PropTypes.node, PropTypes.func], 'Content of the option. In case of function `({ id, value, isSelected, isDisabled, selectedOptions }) => ReactNode`.'),
  value: PropTypes.stringWithDescription('Value of the option.').isRequired,
  searchTerm: PropTypes.stringWithDescription('Term to use to find the option in a searchable select.')
});
SelectOption$1.defaultProps = _objectSpread$4(_objectSpread$4({}, focusableDefaultProps), {}, {
  id: null,
  className: '',
  children: null,
  focusable: false,
  role: 'option',
  disabled: null,
  searchTerm: null
});

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var SelectOption = styled(SelectOption$1).withConfig({
  componentId: "gz-select-1675939618424__sc-1vrr0s9-0"
})(["cursor:pointer;display:flex;padding:3px 12px;.gz-select__option-content{flex:1;pointer-events:none;}&:first-child{padding-top:6px;}&:last-child{padding-bottom:6px;}&[data-active=\"true\"]{background-color:#acb8ff;}&[data-visible=\"false\"]{display:none;}&[aria-selected=\"true\"]{background-color:#314fff;color:#fff;}&[aria-disabled=\"true\"]{cursor:not-allowed;opacity:0.24;}&:focus-within{outline:none;}"]);
SelectOption.displayName = SelectOption$1.displayName;
SelectOption.propTypes = SelectOption$1.propTypes;
SelectOption.defaultProps = SelectOption$1.defaultProps;

function _slicedToArray$6(arr, i) { return _arrayWithHoles$6(arr) || _iterableToArrayLimit$6(arr, i) || _unsupportedIterableToArray$7(arr, i) || _nonIterableRest$6(); }

function _nonIterableRest$6() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray$7(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray$7(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$7(o, minLen); }

function _arrayLikeToArray$7(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit$6(arr, i) { var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]; if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles$6(arr) { if (Array.isArray(arr)) return arr; }
var SelectOptionGroup$1 = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var id = _ref.id,
      className = _ref.className,
      children = _ref.children,
      label = _ref.label,
      disabled = _ref.disabled;

  var _useState = useState(true),
      _useState2 = _slicedToArray$6(_useState, 2),
      isVisible = _useState2[0],
      setIsVisible = _useState2[1];

  var _useSelect = useSelect(),
      filteringTerm = _useSelect.filteringTerm;

  var optionGroupRef = useRef();
  var optionGroupId = useId(id);
  var optionGroupLabelId = "".concat(optionGroupId, "_label");
  var labelToRender = useMemo(function () {
    return isFunction(label) ? label : function () {
      return label;
    };
  }, [label]);
  useEffect(function () {
    var optionsOfGroup = optionGroupRef.current.querySelectorAll('.gz-select__option');
    var optionsNotVisible = [];
    optionsOfGroup.forEach(function (eachOption) {
      var optionId = eachOption.getAttribute('id');
      var rawOptionVisibility = eachOption.dataset.visible;

      if (rawOptionVisibility === 'false') {
        optionsNotVisible.push(optionId);
      }
    });

    if (optionsNotVisible.length === optionsOfGroup.length) {
      setIsVisible(false);
    } else {
      setIsVisible(true);
    }
  }, [filteringTerm]);
  useImperativeHandle(ref, function () {
    return optionGroupRef.current;
  });
  return /*#__PURE__*/jsx(SelectOptionGroupContext.Provider, {
    value: {
      disabled: disabled
    },
    children: /*#__PURE__*/jsxs("div", {
      id: optionGroupId,
      ref: optionGroupRef,
      className: "gz-select__option-group ".concat(className),
      role: "group",
      "data-visible": isVisible,
      "aria-labelledby": optionGroupLabelId,
      "aria-disabled": disabled,
      children: [/*#__PURE__*/jsx("div", {
        id: optionGroupLabelId,
        className: "gz-select__option-group-label",
        children: labelToRender()
      }), children]
    })
  });
});
SelectOptionGroup$1.COMPONENT_ID = 'Gearz.Select.OptionGroup';
SelectOptionGroup$1.displayName = SelectOptionGroup$1.COMPONENT_ID;
SelectOptionGroup$1.propTypes = {
  id: PropTypes.stringWithDescription('Id of the option group.'),
  className: PropTypes.stringWithDescription('Custom class to be applied.'),
  children: PropTypes.nodeWithDescription('Content of the option group.'),
  label: PropTypes.oneOfTypeWithDescription([PropTypes.node, PropTypes.func], 'Label of the option group.'),
  disabled: PropTypes.boolWithDescription('Flag to disable all the options inside the option group.')
};
SelectOptionGroup$1.defaultProps = {
  id: null,
  className: '',
  children: null,
  label: noop,
  disabled: false
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var SelectOptionGroup = styled(SelectOptionGroup$1).withConfig({
  componentId: "gz-select-1675939618424__sc-wvgnjz-0"
})(["cursor:default;.gz-select__option-group-label{padding:6px 12px;}", "{padding-left:24px;}&[data-visible=\"false\"]{display:none;}&[aria-disabled=\"true\"]{.gz-select__option-group-label{cursor:not-allowed;opacity:0.24;}}"], SelectOption);
SelectOptionGroup.displayName = SelectOptionGroup$1.displayName;
SelectOptionGroup.propTypes = SelectOptionGroup$1.propTypes;
SelectOptionGroup.defaultProps = SelectOptionGroup$1.defaultProps;

function _toConsumableArray$1(arr) { return _arrayWithoutHoles$1(arr) || _iterableToArray$1(arr) || _unsupportedIterableToArray$6(arr) || _nonIterableSpread$1(); }

function _nonIterableSpread$1() { throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray$6(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray$6(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$6(o, minLen); }

function _iterableToArray$1(iter) { if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter); }

function _arrayWithoutHoles$1(arr) { if (Array.isArray(arr)) return _arrayLikeToArray$6(arr); }

function _arrayLikeToArray$6(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function ownKeys$3(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$3(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$3(Object(source), true).forEach(function (key) { _defineProperty$3(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$3(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$3(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var getOptionsFromChildren = function getOptionsFromChildren(children, selectId, disabledFromOptionGroup) {
  var childrenAsArray = React.Children.toArray(children);
  var array = [];
  childrenAsArray.forEach(function (each) {
    if (each.type.COMPONENT_ID === SelectOption.COMPONENT_ID) {
      var childrenAsFunction = isFunction(each.props.children) ? each.props.children : function () {
        return each.props.children;
      };
      var dataAttributes = Object.keys(each.props).filter(function (propName) {
        return propName.match(/^data-/gi);
      }).reduce(function (accumulator, currentPropName) {
        return _objectSpread$3(_objectSpread$3({}, accumulator), {}, _defineProperty$3({}, currentPropName, each.props[currentPropName]));
      }, {});
      array.push({
        id: each.props.id || "".concat(selectId, "_").concat(each.props.value),
        value: each.props.value,
        label: childrenAsFunction,
        disabled: disabledFromOptionGroup || each.props.disabled,
        dataAttributes: dataAttributes
      });
    }

    if (each.type.COMPONENT_ID === SelectOptionGroup.COMPONENT_ID) {
      var options = getOptionsFromChildren(each.props.children, selectId, each.props.disabled);
      array = [].concat(_toConsumableArray$1(array), _toConsumableArray$1(options));
    }
  });
  return array;
};

function _toConsumableArray(arr) { return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray$5(arr) || _nonIterableSpread(); }

function _nonIterableSpread() { throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _iterableToArray(iter) { if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter); }

function _arrayWithoutHoles(arr) { if (Array.isArray(arr)) return _arrayLikeToArray$5(arr); }

function _slicedToArray$5(arr, i) { return _arrayWithHoles$5(arr) || _iterableToArrayLimit$5(arr, i) || _unsupportedIterableToArray$5(arr, i) || _nonIterableRest$5(); }

function _nonIterableRest$5() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray$5(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray$5(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$5(o, minLen); }

function _arrayLikeToArray$5(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit$5(arr, i) { var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]; if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles$5(arr) { if (Array.isArray(arr)) return arr; }

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var keyboardInteraction = function keyboardInteraction() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      event = _ref.event,
      selectInputRef = _ref.selectInputRef,
      searchable = _ref.searchable,
      multiple = _ref.multiple,
      isOpened = _ref.isOpened,
      selectTriggerId = _ref.selectTriggerId,
      isControlledSelect = _ref.isControlledSelect,
      activeOptionId = _ref.activeOptionId,
      selectOptions = _ref.selectOptions,
      selectedOptions = _ref.selectedOptions,
      activeOption = _ref.activeOption,
      selectOption = _ref.selectOption,
      deselectOption = _ref.deselectOption,
      openSelect = _ref.openSelect,
      closeSelectIfNeeded = _ref.closeSelectIfNeeded,
      onChange = _ref.onChange,
      filteringTerm = _ref.filteringTerm,
      setFilterTerm = _ref.setFilterTerm;

  var isSpaceKeyPressed = event.code === 'Space' || event.key === ' ' || event.keyCode === 32;
  var isEnterKeyPressed = event.code === 'Enter' || event.key === 'Enter' || event.keyCode === 13;
  var isUpArrowPressed = event.key === 'ArrowUp' || event.code === 'ArrowUp' || event.keyCode === 38;
  var isDownArrowPressed = event.key === 'ArrowDown' || event.code === 'ArrowDown' || event.keyCode === 40;
  var isBackspacePressed = event.code === 'Backspace' || event.key === 'Backspace' || event.keyCode === 8;
  var isCleanButtonPressed = event.target.className.includes('gz-select__clean-button');
  var isCloseTagButtonPressed = event.target.className.includes('gz-tag__close-button');
  var trimFilteringTerm = filteringTerm.trim();

  if (!isOpened && (isSpaceKeyPressed || isUpArrowPressed || isDownArrowPressed || isBackspacePressed)) {
    event.preventDefault();
    openSelect(); // In case of searchable select, we have to focus the input

    if (searchable) {
      selectInputRef.current.focus();
    }

    if (isBackspacePressed && trimFilteringTerm.length === 0 && multiple && searchable) {
      var lastSelectedOption = selectedOptions[selectedOptions.length - 1];

      if (lastSelectedOption) {
        if (!isControlledSelect) {
          deselectOption(lastSelectedOption.id);
        }

        var optionDeselected = selectedOptions.find(function (each) {
          return each.id === lastSelectedOption.id;
        });
        var newValue = selectedOptions.filter(function (each) {
          return each.id !== lastSelectedOption.id;
        }).map(function (each) {
          return each.value;
        });
        var newValueToSend = newValue;

        if (!multiple) {
          var _newValue = _slicedToArray$5(newValue, 1);

          newValueToSend = _newValue[0];
        }

        onChange({
          value: newValueToSend,
          optionDeselected: optionDeselected
        });
      }
    }
  } else if (isOpened && (isSpaceKeyPressed || isUpArrowPressed || isDownArrowPressed || isEnterKeyPressed || isBackspacePressed)) {
    if (isUpArrowPressed || isDownArrowPressed || !searchable && isSpaceKeyPressed) {
      event.preventDefault();
    }

    var filteredSelectOptions = selectOptions.filter(function (each) {
      return !each.disabled && each.isVisible;
    });

    if (isBackspacePressed && trimFilteringTerm.length === 0 && multiple && searchable) {
      var _lastSelectedOption = selectedOptions[selectedOptions.length - 1];

      if (_lastSelectedOption) {
        if (!isControlledSelect) {
          deselectOption(_lastSelectedOption.id);
        }

        var _optionDeselected = selectedOptions.find(function (each) {
          return each.id === _lastSelectedOption.id;
        });

        var _newValue2 = selectedOptions.filter(function (each) {
          return each.id !== _lastSelectedOption.id;
        }).map(function (each) {
          return each.value;
        });

        var _newValueToSend = _newValue2;

        if (!multiple) {
          var _newValue3 = _slicedToArray$5(_newValue2, 1);

          _newValueToSend = _newValue3[0];
        }

        onChange({
          value: _newValueToSend,
          optionDeselected: _optionDeselected
        });
      }
    } else if (isUpArrowPressed || isDownArrowPressed) {
      var activeOptionIndex = filteredSelectOptions.findIndex(function (each) {
        return each.id === activeOptionId;
      });
      var newActiveOptionIndex = activeOptionIndex;

      if (isUpArrowPressed) {
        newActiveOptionIndex = activeOptionIndex - 1;

        if (newActiveOptionIndex < 0) {
          newActiveOptionIndex = 0;
        }
      } else if (isDownArrowPressed) {
        newActiveOptionIndex = activeOptionIndex + 1;

        if (newActiveOptionIndex > filteredSelectOptions.length - 1) {
          newActiveOptionIndex = filteredSelectOptions.length - 1;
        }
      }

      var newActiveOption = filteredSelectOptions.find(function (_, index) {
        return index === newActiveOptionIndex;
      });

      if (newActiveOption) {
        activeOption(newActiveOption.id); // We have to scroll the list element to the new active element

        var optionElement = document.getElementById(newActiveOption.id);
        optionElement.scrollIntoView({
          block: 'nearest'
        });
      }
    } else if (isEnterKeyPressed && !isCleanButtonPressed && !isCloseTagButtonPressed) {
      var currentActiveOption = filteredSelectOptions.find(function (each) {
        return each.id === activeOptionId;
      });

      if (currentActiveOption) {
        if (!selectedOptions.some(function (each) {
          return each.id === currentActiveOption.id;
        })) {
          if (!isControlledSelect) {
            selectOption(currentActiveOption);
          }

          var _newValue4 = [];

          if (multiple) {
            _newValue4 = [].concat(_toConsumableArray(selectedOptions), [currentActiveOption]).map(function (each) {
              return each.value;
            });
          } else {
            _newValue4 = currentActiveOption.value;
          }

          onChange({
            value: _newValue4,
            optionSelected: currentActiveOption
          });
        } else if (multiple) {
          if (!isControlledSelect) {
            deselectOption(currentActiveOption.id);
          }

          var _optionDeselected2 = selectedOptions.find(function (each) {
            return each.id === currentActiveOption.id;
          });

          var _newValue5 = selectedOptions.filter(function (each) {
            return each.id !== currentActiveOption.id;
          }).map(function (each) {
            return each.value;
          });

          onChange({
            value: _newValue5,
            optionDeselected: _optionDeselected2
          });
        }

        closeSelectIfNeeded();
        setFilterTerm(''); // We have to re-focus the trigger to manage properly the focus state

        var selectTrigger = document.getElementById(selectTriggerId);

        if (selectTrigger) {
          // In case of multiple & searchable select, we have to focus the select trigger input
          if (searchable && multiple) {
            var input = selectTrigger.querySelector('.gz-select__input .gz-input');

            if (input) {
              input.focus();
            }
          } else {
            selectTrigger.focus();
          }
        }
      }
    }
  }
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var SelectPlaceholder$1 = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var className = _ref.className;

  var _useSelect = useSelect(),
      selectPlaceholder = _useSelect.selectPlaceholder;

  return /*#__PURE__*/jsx("div", {
    ref: ref,
    className: "gz-select__placeholder ".concat(className),
    children: selectPlaceholder
  });
});
SelectPlaceholder$1.displayName = 'Gearz.Select.Placeholder';
SelectPlaceholder$1.propTypes = {
  className: PropTypes.string
};
SelectPlaceholder$1.defaultProps = {
  className: ''
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var SelectPlaceholder = styled(SelectPlaceholder$1).withConfig({
  componentId: "gz-select-1675939618424__sc-x41xcw-0"
})(["overflow:hidden;text-overflow:ellipsis;white-space:nowrap;"]);
SelectPlaceholder.displayName = SelectPlaceholder$1.displayName;
SelectPlaceholder.propTypes = SelectPlaceholder$1.propTypes;
SelectPlaceholder.defaultProps = SelectPlaceholder$1.defaultProps;

function _slicedToArray$4(arr, i) { return _arrayWithHoles$4(arr) || _iterableToArrayLimit$4(arr, i) || _unsupportedIterableToArray$4(arr, i) || _nonIterableRest$4(); }

function _nonIterableRest$4() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray$4(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray$4(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$4(o, minLen); }

function _arrayLikeToArray$4(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit$4(arr, i) { var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]; if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles$4(arr) { if (Array.isArray(arr)) return arr; }
var SelectValue$1 = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var className = _ref.className,
      input = _ref.input;

  var _useSelect = useSelect(),
      isSelectMultiple = _useSelect.isSelectMultiple,
      isSelectDisabled = _useSelect.isSelectDisabled,
      selectedOptions = _useSelect.selectedOptions,
      deselectOption = _useSelect.deselectOption,
      valueViewerFormatter = _useSelect.valueViewerFormatter,
      onChange = _useSelect.onChange;

  var deselectOptionHandler = useCallback(function (optionId) {
    var optionDeselected = selectedOptions.find(function (each) {
      return each.id === optionId;
    });
    var newSelectedOptions = selectedOptions.filter(function (each) {
      return each.id !== optionId;
    }).map(function (each) {
      return each.value;
    });
    deselectOption(optionId);
    var newValue = newSelectedOptions;

    if (!isSelectMultiple) {
      var _newSelectedOptions = _slicedToArray$4(newSelectedOptions, 1);

      newValue = _newSelectedOptions[0];
    }

    onChange({
      value: newValue,
      optionDeselected: optionDeselected
    });
  }, [isSelectMultiple, selectedOptions, onChange, deselectOption]);
  return /*#__PURE__*/jsxs("div", {
    ref: ref,
    className: "gz-select__value ".concat(className),
    "data-multiple": isSelectMultiple,
    children: [selectedOptions.length > 0 && valueViewerFormatter({
      selectedOptions: selectedOptions,
      isSelectDisabled: isSelectDisabled,
      isSelectMultiple: isSelectMultiple,
      deselectOption: deselectOptionHandler
    }), isSelectMultiple && input]
  });
});
SelectValue$1.displayName = 'Gearz.Select.Value';
SelectValue$1.propTypes = {
  className: PropTypes.string,
  input: PropTypes.node
};
SelectValue$1.defaultProps = {
  className: '',
  input: null
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var SelectValue = styled(SelectValue$1).withConfig({
  componentId: "gz-select-1675939618424__sc-17nl07m-0"
})(["overflow:hidden;text-overflow:ellipsis;white-space:nowrap;&[data-multiple=\"true\"]{display:flex;flex-wrap:wrap;gap:10px;overflow:initial;text-overflow:initial;white-space:normal;width:100%;.gz-select__input{position:relative;width:1ch;&:first-child{width:100%;}}}"]);
SelectValue.displayName = SelectValue$1.displayName;
SelectValue.propTypes = SelectValue$1.propTypes;
SelectValue.defaultProps = SelectValue$1.defaultProps;

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */

var SelectArrow$1 = function SelectArrow(_ref) {
  var className = _ref.className;

  var _useSelect = useSelect(),
      isSelectOpened = _useSelect.isSelectOpened,
      isSelectDisabled = _useSelect.isSelectDisabled,
      isSelectFocused = _useSelect.isSelectFocused,
      isSelectInvalid = _useSelect.isSelectInvalid,
      isSelectMultiple = _useSelect.isSelectMultiple,
      isSelectSearchable = _useSelect.isSelectSearchable,
      selectedOptions = _useSelect.selectedOptions,
      arrowRenderer = _useSelect.arrowRenderer;

  var arrowContent;

  if (isFunction(arrowRenderer)) {
    arrowContent = arrowRenderer({
      isOpened: isSelectOpened,
      isFocused: isSelectFocused,
      disabled: isSelectDisabled,
      invalid: isSelectInvalid,
      multiple: isSelectMultiple,
      searchable: isSelectSearchable,
      selectedOptions: selectedOptions
    });
  } else {
    var iconName = 'angle-down';
    var iconFallback = angleDown;

    if (isSelectOpened) {
      iconName = 'angle-up';
      iconFallback = angleUp;
    }

    arrowContent = /*#__PURE__*/jsx(Icon, {
      "aria-hidden": "true",
      "aria-disabled": isSelectDisabled,
      className: "gz-select__arrow",
      name: iconName,
      fallback: iconFallback
    });
  }

  return arrowContent && /*#__PURE__*/jsx("div", {
    className: "gz-select__arrow-container ".concat(className),
    children: arrowContent
  });
};

SelectArrow$1.displayName = 'Gearz.Select.Arrow';
SelectArrow$1.propTypes = {
  className: PropTypes.string
};
SelectArrow$1.defaultProps = {
  className: ''
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var SelectArrow = styled(SelectArrow$1).withConfig({
  componentId: "gz-select-1675939618424__sc-1cfpt0x-0"
})(["cursor:pointer;display:flex;height:20px;position:absolute;right:6px;text-align:center;top:calc(50% - 10px);width:20px;.gz-select__arrow{height:20px;width:20px;&[aria-disabled=\"true\"]{cursor:not-allowed;}}"]);
SelectArrow.displayName = SelectArrow$1.displayName;
SelectArrow.propTypes = SelectArrow$1.propTypes;
SelectArrow.defaultProps = SelectArrow$1.defaultProps;

function _slicedToArray$3(arr, i) { return _arrayWithHoles$3(arr) || _iterableToArrayLimit$3(arr, i) || _unsupportedIterableToArray$3(arr, i) || _nonIterableRest$3(); }

function _nonIterableRest$3() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray$3(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray$3(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$3(o, minLen); }

function _arrayLikeToArray$3(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit$3(arr, i) { var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]; if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles$3(arr) { if (Array.isArray(arr)) return arr; }
var INPUT_SIZE_MARGIN = 6;
var SelectInput$1 = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var className = _ref.className;
  var selectValueRef = useRef();
  var selectInputRef = useRef();

  var _useState = useState(''),
      _useState2 = _slicedToArray$3(_useState, 2),
      searchText = _useState2[0],
      setSearchText = _useState2[1];

  var _useSelect = useSelect(),
      selectedOptions = _useSelect.selectedOptions,
      selectPlaceholder = _useSelect.selectPlaceholder,
      isSelectOpened = _useSelect.isSelectOpened,
      isSelectFocused = _useSelect.isSelectFocused,
      isSelectMultiple = _useSelect.isSelectMultiple,
      filteringTerm = _useSelect.filteringTerm,
      searchByTerm = _useSelect.searchByTerm,
      openSelect = _useSelect.openSelect,
      setFilterTerm = _useSelect.setFilterTerm;

  var numberOfSelectedOptions = selectedOptions.length;
  var shouldShowPlaceholder = useMemo(function () {
    if (isSelectMultiple) {
      return numberOfSelectedOptions === 0;
    }

    return !searchText && numberOfSelectedOptions === 0;
  }, [isSelectMultiple, searchText, numberOfSelectedOptions]);
  var shouldShowValue = useMemo(function () {
    if (isSelectMultiple) {
      // In case of multiple, we show the value because it will manage the input
      return true;
    }

    return !searchText && numberOfSelectedOptions > 0;
  }, [isSelectMultiple, searchText, numberOfSelectedOptions]);
  var onChangeHandler = useCallback(function (_ref2) {
    var newFilteringTerm = _ref2.value;
    searchByTerm(newFilteringTerm);
    setFilterTerm(newFilteringTerm);
    setSearchText(newFilteringTerm);

    if (!isSelectOpened && isSelectFocused) {
      openSelect();
    }

    if (isSelectMultiple) {
      if (newFilteringTerm.length === 0 && numberOfSelectedOptions === 0) {
        selectInputRef.current.style.width = '100%';
      } else {
        var _selectValueRef$curre = selectValueRef.current.getBoundingClientRect(),
            selectValueWidth = _selectValueRef$curre.width;

        var tmpElement = document.createElement('div');
        tmpElement.style.opacity = 0;
        tmpElement.style.width = "calc(".concat(newFilteringTerm.length, "ch + ").concat(INPUT_SIZE_MARGIN, "px)");
        document.body.append(tmpElement);

        var _tmpElement$getBoundi = tmpElement.getBoundingClientRect(),
            tmpElementWidth = _tmpElement$getBoundi.width;

        document.body.removeChild(tmpElement);

        if (tmpElementWidth >= selectValueWidth) {
          selectInputRef.current.style.width = '100%';
        } else {
          selectInputRef.current.style.width = "calc(".concat(newFilteringTerm.length, "ch + ").concat(INPUT_SIZE_MARGIN, "px)");
        }
      }
    }
  }, [numberOfSelectedOptions, isSelectMultiple, isSelectFocused, isSelectOpened, openSelect, searchByTerm, setFilterTerm]);
  var onBlurHandler = useCallback(function () {
    if (!isSelectMultiple || isSelectMultiple && numberOfSelectedOptions === 0) {
      selectInputRef.current.style.width = '100%';
    } else {
      selectInputRef.current.style.width = "calc(".concat(filteringTerm.length, "ch + ").concat(INPUT_SIZE_MARGIN, "px)");
    }
  }, [filteringTerm, isSelectMultiple, numberOfSelectedOptions]);
  var previousIsSelectOpened = usePrevious(isSelectOpened);
  var previousSelectedOptions = usePrevious(selectedOptions);
  useEffect(function () {
    if (!isSelectOpened && previousIsSelectOpened) {
      // on close the select, we have to clean the current search term
      setSearchText('');
      setFilterTerm('');
    }
  }, [previousIsSelectOpened, isSelectOpened, setFilterTerm]);
  useEffect(function () {
    var finalPreviousSelectedOptions = Array.isArray(previousSelectedOptions) ? previousSelectedOptions : [];

    if (isSelectMultiple && (selectedOptions.length > finalPreviousSelectedOptions.length || selectedOptions.length < finalPreviousSelectedOptions.length)) {
      if (selectedOptions.length === 0) {
        // In case of no selected option, we have to restore the width of the input
        selectInputRef.current.style.width = '100%';
      } else {
        // In case of option selected via keyboard, we have to set input width
        selectInputRef.current.style.width = "calc(".concat(filteringTerm.length, "ch + ").concat(INPUT_SIZE_MARGIN, "px)");
      }
    }
  }, [filteringTerm, previousSelectedOptions, selectedOptions, isSelectMultiple, setFilterTerm]);
  useEffect(function () {
    setSearchText(filteringTerm);
  }, [filteringTerm]);
  useImperativeHandle(ref, function () {
    if (selectInputRef.current) {
      var input = selectInputRef.current.querySelector('input');
      return input;
    }

    return null;
  });

  var searchableInput = /*#__PURE__*/jsx(InputText, {
    ref: selectInputRef,
    focusable: isSelectFocused,
    className: "gz-select__input",
    value: searchText,
    placeholder: shouldShowPlaceholder ? selectPlaceholder : null,
    onBlur: onBlurHandler,
    onChange: onChangeHandler
  });

  return /*#__PURE__*/jsxs("div", {
    className: "gz-select_input-wrapper ".concat(className),
    "data-multiple": isSelectMultiple,
    children: [shouldShowValue && /*#__PURE__*/jsx(SelectValue, {
      ref: selectValueRef,
      className: "gz-select__input-value",
      input: searchableInput
    }), !isSelectMultiple && searchableInput]
  });
});
SelectInput$1.displayName = 'Gearz.Select.Input';
SelectInput$1.propTypes = {
  className: PropTypes.string
};
SelectInput$1.defaultProps = {
  className: ''
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var SelectInput = styled(SelectInput$1).withConfig({
  componentId: "gz-select-1675939618424__sc-1h4kvt0-0"
})(["align-items:center;display:flex;flex:1;min-height:20px;overflow:hidden;position:relative;.gz-select__input{background-color:transparent;border:none;border-radius:0;left:0;position:absolute;.gz-input{background-color:transparent;padding:0;&:placeholder-shown{text-overflow:ellipsis;}}&:focus-within{outline:none;}}&[data-multiple=\"true\"]{.gz-select__input{position:relative;width:1ch;&:first-child{width:100%;}}}"]);
SelectInput.displayName = SelectInput$1.displayName;
SelectInput.propTypes = SelectInput$1.propTypes;
SelectInput.defaultProps = SelectInput$1.defaultProps;

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */

var SelectCleanButton$1 = function SelectCleanButton(_ref) {
  var className = _ref.className;

  var _useSelect = useSelect(),
      isControlledSelect = _useSelect.isControlledSelect,
      isSelectMultiple = _useSelect.isSelectMultiple,
      isSelectDisabled = _useSelect.isSelectDisabled,
      onChange = _useSelect.onChange,
      setFilterTerm = _useSelect.setFilterTerm,
      deselectAllOptions = _useSelect.deselectAllOptions;

  var onClickHandler = function onClickHandler() {
    if (!isSelectDisabled) {
      if (!isControlledSelect) {
        deselectAllOptions();
      }

      var newValue = isSelectMultiple ? [] : '';
      setFilterTerm('');
      onChange({
        value: newValue
      });
    }
  };

  return /*#__PURE__*/jsx("button", {
    type: "button",
    className: "gz-select__clean-button ".concat(className),
    disabled: isSelectDisabled,
    onClick: onClickHandler,
    children: /*#__PURE__*/jsx(Icon, {
      "aria-hidden": "true",
      "aria-disabled": isSelectDisabled,
      name: "close",
      fallback: close
    })
  });
};

SelectCleanButton$1.displayName = 'Gearz.Select.CleanButton';
SelectCleanButton$1.propTypes = {
  className: PropTypes.string
};
SelectCleanButton$1.defaultProps = {
  className: ''
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var SelectCleanButton = styled(SelectCleanButton$1).withConfig({
  componentId: "gz-select-1675939618424__sc-7yxqk7-0"
})(["align-items:center;appearance:none;background-color:transparent;border:none;cursor:pointer;display:flex;height:16px;justify-content:center;margin:0;padding:0;position:absolute;right:28px;top:calc(50% - 8px);width:16px;&:disabled{cursor:not-allowed;}.gz-icon{height:16px;pointer-events:none;width:16px;&[aria-disabled=\"true\"]{cursor:not-allowed;}}"]);
SelectCleanButton.displayName = SelectCleanButton$1.displayName;
SelectCleanButton.propTypes = SelectCleanButton$1.propTypes;
SelectCleanButton.defaultProps = SelectCleanButton$1.defaultProps;

var _excluded$2 = ["className", "rawOptions"];

function ownKeys$2(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$2(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$2(Object(source), true).forEach(function (key) { _defineProperty$2(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$2(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$2(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _objectWithoutProperties$2(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose$2(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose$2(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
var SelectTrigger$1 = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var className = _ref.className,
      rawOptions = _ref.rawOptions,
      otherProps = _objectWithoutProperties$2(_ref, _excluded$2);

  var ariaAttributes = useAriaAttributes(otherProps, {
    excludedAttributes: ['aria-live', 'aria-invalid', 'aria-haspopup', 'aria-owns', 'aria-activedescendant', 'aria-expanded']
  });
  var dataAttributes = useDataAttributes(otherProps, {
    excludedAttributes: ['data-focused', 'data-clearable', 'data-searchable']
  });

  var _useSelect = useSelect(),
      isControlledSelect = _useSelect.isControlledSelect,
      activeOptionId = _useSelect.activeOptionId,
      selectTriggerRef = _useSelect.selectTriggerRef,
      selectTriggerId = _useSelect.selectTriggerId,
      selectListId = _useSelect.selectListId,
      selectInputRef = _useSelect.selectInputRef,
      selectOptions = _useSelect.selectOptions,
      selectedOptions = _useSelect.selectedOptions,
      isSelectOpened = _useSelect.isSelectOpened,
      isSelectDisabled = _useSelect.isSelectDisabled,
      isSelectFocused = _useSelect.isSelectFocused,
      isSelectClearable = _useSelect.isSelectClearable,
      isSelectMultiple = _useSelect.isSelectMultiple,
      isSelectSearchable = _useSelect.isSelectSearchable,
      isSelectInvalid = _useSelect.isSelectInvalid,
      selectName = _useSelect.selectName,
      selectValue = _useSelect.selectValue,
      selectPrefix = _useSelect.selectPrefix,
      selectDefaultValue = _useSelect.selectDefaultValue,
      selectAutoFocus = _useSelect.selectAutoFocus,
      filteringTerm = _useSelect.filteringTerm,
      setFilterTerm = _useSelect.setFilterTerm,
      activeOption = _useSelect.activeOption,
      selectOption = _useSelect.selectOption,
      deselectOption = _useSelect.deselectOption,
      deselectOptionByValue = _useSelect.deselectOptionByValue,
      openSelect = _useSelect.openSelect,
      closeSelect = _useSelect.closeSelect,
      closeSelectIfNeeded = _useSelect.closeSelectIfNeeded,
      onFocusContainerHandler = _useSelect.onFocusContainerHandler,
      onBlurContainerHandler = _useSelect.onBlurContainerHandler,
      onChange = _useSelect.onChange;

  var focusableProps = useFocusable(selectTriggerRef.current, _objectSpread$2(_objectSpread$2({}, otherProps), {}, {
    focusable: false,
    disabled: isSelectDisabled
  }));
  var onKeyDownHandler = useCallback(function (event) {
    keyboardInteraction({
      event: event,
      selectTriggerId: selectTriggerId,
      searchable: isSelectSearchable,
      multiple: isSelectMultiple,
      selectInputRef: selectInputRef,
      isOpened: isSelectOpened,
      isControlledSelect: isControlledSelect,
      selectOptions: selectOptions,
      selectedOptions: selectedOptions,
      activeOptionId: activeOptionId,
      filteringTerm: filteringTerm,
      setFilterTerm: setFilterTerm,
      activeOption: activeOption,
      selectOption: selectOption,
      deselectOption: deselectOption,
      openSelect: openSelect,
      closeSelectIfNeeded: closeSelectIfNeeded,
      onChange: onChange
    }); // eslint-disable-next-line max-len
  }, [filteringTerm, selectInputRef, isSelectSearchable, selectTriggerId, isSelectMultiple, selectOptions, selectedOptions, activeOptionId, isSelectOpened, isControlledSelect, activeOption, selectOption, deselectOption, openSelect, closeSelectIfNeeded, setFilterTerm, onChange]);
  var shouldHiddenInputFocusable = useMemo(function () {
    if (!isSelectFocused) {
      if (isSelectSearchable && isSelectMultiple && selectedOptions.length > 0) {
        return -1;
      }

      if ((isSelectClearable || isSelectMultiple) && selectedOptions.length > 0) {
        return -1;
      }

      return 0;
    }

    return -1;
  }, [isSelectFocused, isSelectClearable, isSelectSearchable, isSelectMultiple, selectedOptions]);
  var onClickHandler = useCallback(function (event) {
    var isCloseTagClicked = !!event.target.closest('.gz-tag');
    var isCleanButtonClicked = !!event.target.closest('.gz-select__clean-button');

    if (!isCloseTagClicked && !isCleanButtonClicked) {
      if (!filteringTerm) {
        if (!isSelectOpened) {
          openSelect();
        } else {
          closeSelect();
        }
      } // In case of searchable select, we have to focus the input


      if (isSelectSearchable) {
        selectInputRef.current.focus();
      }
    }
  }, [isSelectOpened, selectInputRef, isSelectSearchable, filteringTerm, openSelect, closeSelect]);
  var onBlurHandler = useCallback(function (event) {
    var isOutsideTrigger = selectTriggerRef.current && !selectTriggerRef.current.contains(event.relatedTarget);
    var isOutsideList = true;
    var selectListElement = document.getElementById(selectListId);

    if (selectListElement) {
      isOutsideList = !selectListElement.contains(event.relatedTarget);
    }

    if (isSelectOpened && isOutsideList && isOutsideTrigger) {
      closeSelect();
    }

    onBlurContainerHandler(event);
  }, [selectTriggerRef, selectListId, isSelectOpened, closeSelect, onBlurContainerHandler]);
  var onFocusHiddenInput = useCallback(function () {
    if (isSelectSearchable) {
      selectInputRef.current.focus();
    }
  }, [isSelectSearchable, selectInputRef]);
  var previousValue = usePrevious(selectValue);
  useEffect(function () {
    if (selectAutoFocus) {
      selectTriggerRef.current.focus();
    }
  }, [selectTriggerRef, selectAutoFocus]);
  useEffect(function () {
    // We set the defaultValue only we don't have any selected option
    if (isDefinedAndNotNull(selectDefaultValue) && selectedOptions.length === 0) {
      var defaultValueAsArray = Array.isArray(selectDefaultValue) ? selectDefaultValue : [selectDefaultValue];
      defaultValueAsArray.forEach(function (each) {
        var option = rawOptions.find(function (rawOption) {
          return rawOption.value === each;
        });

        if (option) {
          selectOption(option);

          if (!activeOptionId) {
            activeOption(option.id);
          }
        }
      });
    }
  }, [activeOptionId, rawOptions, selectDefaultValue, selectedOptions]);
  useEffect(function () {
    if (isDefinedAndNotNull(selectValue)) {
      var previousValueAsArray = Array.isArray(previousValue) ? previousValue : [previousValue];
      var valueAsArray = Array.isArray(selectValue) ? selectValue : [selectValue];
      valueAsArray.forEach(function (each) {
        var optionsToUse = isSelectOpened ? selectOptions : rawOptions;
        var optionToSelect = optionsToUse.find(function (rawOption) {
          return rawOption.value === each;
        });

        if (optionToSelect) {
          selectOption(optionToSelect); // ToDo: check the order of active option

          if (!activeOptionId) {
            activeOption(optionToSelect.id);
          }
        }
      });
      var removedPreviousValues = previousValueAsArray.filter(function (each) {
        return !valueAsArray.includes(each);
      });
      removedPreviousValues.forEach(function (each) {
        var optionsToUse = isSelectOpened ? selectOptions : rawOptions;
        var optionToDeselect = optionsToUse.find(function (rawOption) {
          return rawOption.value === each;
        });

        if (optionToDeselect) {
          deselectOption(optionToDeselect.id);
        } else {
          deselectOptionByValue(each);
        }
      });
    }
  }, [activeOptionId, isSelectOpened, selectOptions, rawOptions, previousValue, selectValue]);
  useImperativeHandle(ref, function () {
    return selectTriggerRef.current;
  });
  return (
    /*#__PURE__*/
    // eslint-disable-next-line jsx-a11y/aria-activedescendant-has-tabindex
    jsxs("div", _objectSpread$2(_objectSpread$2(_objectSpread$2(_objectSpread$2({
      id: selectTriggerId,
      className: "gz-select__trigger ".concat(className),
      ref: selectTriggerRef,
      onClick: onClickHandler,
      onKeyDown: onKeyDownHandler,
      onFocus: onFocusContainerHandler,
      onBlur: onBlurHandler // eslint-disable-next-line react/jsx-props-no-spreading

    }, ariaAttributes), dataAttributes), {}, {
      "aria-live": "polite",
      "aria-invalid": isSelectInvalid,
      "aria-haspopup": "listbox",
      "aria-owns": selectListId,
      "aria-activedescendant": activeOptionId,
      "aria-expanded": isSelectOpened,
      "data-focused": isSelectFocused,
      "data-clearable": isSelectClearable,
      "data-searchable": isSelectSearchable // eslint-disable-next-line react/jsx-props-no-spreading

    }, focusableProps), {}, {
      children: [/*#__PURE__*/jsx("input", {
        className: "gz-select__trigger-input-hidden",
        name: selectName,
        "aria-hidden": "true",
        tabIndex: shouldHiddenInputFocusable,
        defaultValue: selectedOptions.map(function (each) {
          return each.value;
        }).join(','),
        onFocus: onFocusHiddenInput
      }), isDefinedAndNotNull(selectPrefix) && /*#__PURE__*/jsx("div", {
        className: "gz-select__trigger-prefix-container",
        children: selectPrefix
      }), !isSelectSearchable && /*#__PURE__*/jsxs(Fragment, {
        children: [selectedOptions.length === 0 && /*#__PURE__*/jsx(SelectPlaceholder, {}), selectedOptions.length > 0 && /*#__PURE__*/jsx(SelectValue, {})]
      }), isSelectSearchable && /*#__PURE__*/jsx(SelectInput, {
        ref: selectInputRef
      }), isSelectClearable && (selectedOptions.length > 0 || filteringTerm.length > 0) && /*#__PURE__*/jsx(SelectCleanButton, {}), /*#__PURE__*/jsx(SelectArrow, {})]
    }))
  );
});
SelectTrigger$1.displayName = 'Gearz.Select.Trigger';
SelectTrigger$1.propTypes = _objectSpread$2(_objectSpread$2({}, focusablePropTypes), {}, {
  className: PropTypes.string
});
SelectTrigger$1.defaultProps = _objectSpread$2(_objectSpread$2({}, focusableDefaultProps), {}, {
  className: '',
  role: 'combobox'
});

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var SelectTrigger = styled(SelectTrigger$1).withConfig({
  componentId: "gz-select-1675939618424__sc-ksaenr-0"
})(["align-items:center;background-color:#fff;border:1px solid #333;border-radius:4px;cursor:pointer;display:flex;flex:1;min-height:40px;padding:6px 30px 6px 6px;position:relative;width:100%;.gz-select__trigger-input-hidden{bottom:0;left:0;opacity:0;position:absolute;pointer-events:none;width:100%;}.gz-select__trigger-prefix-container{margin-right:6px;}&:not([aria-disabled=\"true\"]){&[aria-invalid=\"true\"]{border:1px solid #f00314;}&[data-focused=\"true\"],&:focus-within{outline:2px solid #314fff;outline-offset:2px;}}&[aria-disabled=\"true\"]{cursor:not-allowed;opacity:0.24;user-select:none;}&[data-searchable=\"true\"]{cursor:text;}&[data-clearable=\"true\"]{padding-right:48px;}"]);
SelectTrigger.displayName = SelectTrigger$1.displayName;
SelectTrigger.propTypes = SelectTrigger$1.propTypes;
SelectTrigger.defaultProps = SelectTrigger$1.defaultProps;

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */

var SelectNoResultsOption$1 = function SelectNoResultsOption(_ref) {
  var className = _ref.className,
      children = _ref.children;
  return /*#__PURE__*/jsx("div", {
    className: "gz-select__no-results-option ".concat(className),
    children: children
  });
};

SelectNoResultsOption$1.displayName = 'Gearz.Select.NoResultsOption';
SelectNoResultsOption$1.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node
};
SelectNoResultsOption$1.defaultProps = {
  className: '',
  children: null
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var SelectNoResultsOption = styled(SelectNoResultsOption$1).withConfig({
  componentId: "gz-select-1675939618424__sc-okf0wx-0"
})(["user-select:none;&:focus-within{outline:none;}"]);
SelectNoResultsOption.displayName = SelectNoResultsOption$1.displayName;
SelectNoResultsOption.propTypes = SelectNoResultsOption$1.propTypes;
SelectNoResultsOption.defaultProps = SelectNoResultsOption$1.defaultProps;

function _slicedToArray$2(arr, i) { return _arrayWithHoles$2(arr) || _iterableToArrayLimit$2(arr, i) || _unsupportedIterableToArray$2(arr, i) || _nonIterableRest$2(); }

function _nonIterableRest$2() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray$2(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray$2(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$2(o, minLen); }

function _arrayLikeToArray$2(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit$2(arr, i) { var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]; if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles$2(arr) { if (Array.isArray(arr)) return arr; }
var SelectList$1 = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var className = _ref.className,
      children = _ref.children,
      direction = _ref.direction;
  var selectListRef = useRef();

  var _useSelect = useSelect(),
      selectListId = _useSelect.selectListId,
      selectTriggerId = _useSelect.selectTriggerId,
      selectOptions = _useSelect.selectOptions,
      selectedOptions = _useSelect.selectedOptions,
      isSelectFocused = _useSelect.isSelectFocused,
      isSelectOpened = _useSelect.isSelectOpened,
      isSelectMultiple = _useSelect.isSelectMultiple,
      isSelectSearchable = _useSelect.isSelectSearchable,
      isControlledSelect = _useSelect.isControlledSelect,
      activeOptionId = _useSelect.activeOptionId,
      messageOnNoResults = _useSelect.messageOnNoResults,
      filteringTerm = _useSelect.filteringTerm,
      setFilterTerm = _useSelect.setFilterTerm,
      openSelect = _useSelect.openSelect,
      closeSelectIfNeeded = _useSelect.closeSelectIfNeeded,
      selectOption = _useSelect.selectOption,
      deselectOption = _useSelect.deselectOption,
      activeOption = _useSelect.activeOption,
      onFocusContainerHandler = _useSelect.onFocusContainerHandler,
      onBlurContainerHandler = _useSelect.onBlurContainerHandler,
      onChange = _useSelect.onChange;

  var onKeyDownHandler = useCallback(function (event) {
    keyboardInteraction({
      event: event,
      selectTriggerId: selectTriggerId,
      searchable: isSelectSearchable,
      multiple: isSelectMultiple,
      selectListRef: selectListRef,
      isOpened: isSelectOpened,
      filteringTerm: filteringTerm,
      isControlledSelect: isControlledSelect,
      selectOptions: selectOptions,
      selectedOptions: selectedOptions,
      activeOptionId: activeOptionId,
      activeOption: activeOption,
      selectOption: selectOption,
      deselectOption: deselectOption,
      openSelect: openSelect,
      closeSelectIfNeeded: closeSelectIfNeeded,
      setFilterTerm: setFilterTerm,
      onChange: onChange
    });
  }, [selectTriggerId, filteringTerm, isSelectMultiple, isSelectSearchable, selectOptions, selectedOptions, activeOptionId, isControlledSelect, isSelectOpened, activeOption, selectOption, deselectOption, openSelect, closeSelectIfNeeded, setFilterTerm, onChange]);
  var shouldShowNoResults = useMemo(function () {
    return selectOptions.every(function (each) {
      return !each.isVisible;
    });
  }, [selectOptions]);
  var messageOnNoResultsToRender = useMemo(function () {
    return isFunction(messageOnNoResults) ? messageOnNoResults : function () {
      return messageOnNoResults;
    };
  }, [messageOnNoResults]);
  var previousIsSelectOpened = usePrevious(isSelectOpened);
  useEffect(function () {
    if (isSelectOpened && !previousIsSelectOpened) {
      var optionIdToActivate = activeOptionId;

      if (!activeOptionId) {
        if (selectedOptions.length) {
          var _selectedOptions = _slicedToArray$2(selectedOptions, 1),
              firstSelectedOption = _selectedOptions[0];

          if (firstSelectedOption.id) {
            optionIdToActivate = firstSelectedOption.id;
          }
        } else {
          var _ref2 = selectOptions.find(function (each) {
            return !each.disabled;
          }) || {};

          optionIdToActivate = _ref2.id;
        }
      } else if (selectedOptions.length) {
        var _selectedOptions2 = _slicedToArray$2(selectedOptions, 1),
            _firstSelectedOption = _selectedOptions2[0];

        if (_firstSelectedOption.id) {
          optionIdToActivate = _firstSelectedOption.id;
        }
      }

      activeOption(optionIdToActivate);

      if (!isSelectMultiple) {
        // We have to scroll the list element to the new active element
        var optionElement = document.getElementById(optionIdToActivate);

        if (optionElement) {
          optionElement.scrollIntoView({
            block: 'center'
          });
        }
      }
    }
  }, [isSelectOpened, isSelectMultiple, previousIsSelectOpened, activeOptionId, selectOptions, selectedOptions, activeOption]);
  useImperativeHandle(ref, function () {
    return selectListRef.current;
  });
  return /*#__PURE__*/jsxs("div", {
    id: selectListId,
    ref: selectListRef,
    className: "gz-select__list ".concat(className),
    role: "listbox",
    onKeyDown: onKeyDownHandler,
    tabIndex: "-1",
    onFocus: onFocusContainerHandler,
    onBlur: onBlurContainerHandler,
    "data-focused": isSelectFocused,
    "data-placement": direction,
    children: [children, shouldShowNoResults && /*#__PURE__*/jsx(SelectNoResultsOption, {
      children: messageOnNoResultsToRender({
        selectOptions: selectOptions
      })
    })]
  });
});
SelectList$1.COMPONENT_ID = 'Gearz.Select.List';
SelectList$1.displayName = SelectList$1.COMPONENT_ID;
SelectList$1.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  direction: PropTypes.oneOf(Object.values(DIRECTIONS))
};
SelectList$1.defaultProps = {
  className: '',
  children: null,
  direction: DIRECTIONS.DOWN
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var SelectList = styled(SelectList$1).withConfig({
  componentId: "gz-select-1675939618424__sc-10qhlic-0"
})(["border:1px solid #333;border-radius:4px;max-height:250px;overflow:auto;&:focus-within{outline:none;}&[data-placement=\"down\"]{margin-top:6px;}&[data-placement=\"up\"]{margin-bottom:6px;}", ""], function (props) {
  return getThemeByProps({
    componentId: SelectList$1.COMPONENT_ID,
    props: props
  });
});
SelectList.displayName = SelectList$1.displayName;
SelectList.propTypes = SelectList$1.propTypes;
SelectList.defaultProps = SelectList$1.defaultProps;

var _excluded$1 = ["className", "children"];

function ownKeys$1(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$1(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$1(Object(source), true).forEach(function (key) { _defineProperty$1(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$1(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$1(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _slicedToArray$1(arr, i) { return _arrayWithHoles$1(arr) || _iterableToArrayLimit$1(arr, i) || _unsupportedIterableToArray$1(arr, i) || _nonIterableRest$1(); }

function _nonIterableRest$1() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray$1(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray$1(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$1(o, minLen); }

function _arrayLikeToArray$1(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit$1(arr, i) { var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]; if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles$1(arr) { if (Array.isArray(arr)) return arr; }

function _objectWithoutProperties$1(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose$1(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose$1(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
var Select$1 = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var className = _ref.className,
      children = _ref.children,
      otherProps = _objectWithoutProperties$1(_ref, _excluded$1);

  var _useSelect = useSelect(),
      selectId = _useSelect.selectId,
      selectAppendTo = _useSelect.selectAppendTo,
      disableBackgroundScroll = _useSelect.disableBackgroundScroll,
      avoidReposition = _useSelect.avoidReposition,
      forceOpenEvenNotFit = _useSelect.forceOpenEvenNotFit,
      forceDefaultCloseMethod = _useSelect.forceDefaultCloseMethod,
      isSelectOpened = _useSelect.isSelectOpened,
      isSelectDisabled = _useSelect.isSelectDisabled,
      selectDirection = _useSelect.selectDirection,
      selectTriggerRef = _useSelect.selectTriggerRef,
      selectListRef = _useSelect.selectListRef,
      closeSelect = _useSelect.closeSelect,
      onOpened = _useSelect.onOpened,
      onClosed = _useSelect.onClosed;

  var _useState = useState(function () {
    return selectDirection === StyledLinkedFloatingElement.PLACEMENTS.BOTTOM ? DIRECTIONS.DOWN : DIRECTIONS.UP;
  }),
      _useState2 = _slicedToArray$1(_useState, 2),
      listDirection = _useState2[0],
      setListDirection = _useState2[1];

  var setRefs = useCallback(function (linkedRef) {
    if (linkedRef) {
      selectTriggerRef.current = linkedRef.relatedTo;
      selectListRef.current = linkedRef.linkedFloatingElement;
    }
  }, [selectListRef, selectTriggerRef]);
  var onClickOutside = useCallback(function () {
    if (!isSelectDisabled) {
      closeSelect();
    }
  }, [isSelectDisabled, closeSelect]);
  var onPlacementChangedHandler = useCallback(function (newPlacement) {
    var finalNewPlacement = newPlacement === StyledLinkedFloatingElement.PLACEMENTS.BOTTOM ? DIRECTIONS.DOWN : DIRECTIONS.UP;
    setListDirection(finalNewPlacement);
  }, []);
  var rawOptions = useMemo(function () {
    return getOptionsFromChildren(children, selectId);
  }, [children, selectId]);
  useImperativeHandle(ref, function () {
    return selectTriggerRef.current;
  });
  return /*#__PURE__*/jsx(StyledLinkedFloatingElement, {
    ref: setRefs,
    animation: "none",
    appendTo: selectAppendTo,
    content: /*#__PURE__*/jsx(SelectList, {
      direction: listDirection,
      children: children
    }),
    sameWidthAsChildren: true,
    trigger: StyledLinkedFloatingElement.TRIGGERS.MANUAL,
    opened: isSelectOpened,
    disableBackgroundScroll: disableBackgroundScroll,
    avoidReposition: avoidReposition,
    forceOpenEvenNotFit: forceOpenEvenNotFit,
    forceDefaultCloseMethod: forceDefaultCloseMethod,
    placement: selectDirection,
    forceViewportPlacement: true,
    onOpened: onOpened,
    onClose: closeSelect,
    onClosed: onClosed,
    onClickOutside: onClickOutside,
    onPlacementChanged: onPlacementChangedHandler,
    children: /*#__PURE__*/jsx(SelectTrigger, _objectSpread$1({
      className: className,
      focusable: !isSelectDisabled,
      rawOptions: rawOptions
    }, otherProps))
  });
});
Select$1.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node
};
Select$1.defaultProps = {
  className: '',
  children: null
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var Select = styled(Select$1).withConfig({
  componentId: "gz-select-1675939618424__sc-l9tber-0"
})([""]);
Select.displayName = Select$1.displayName;
Select.propTypes = Select$1.propTypes;
Select.defaultProps = Select$1.defaultProps;

var _excluded = ["className", "children", "id"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit(arr, i) { var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]; if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
var SelectProvider$1 = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var className = _ref.className,
      children = _ref.children,
      propId = _ref.id,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  var ariaAttributes = useAriaAttributes(otherProps);
  var dataAttributes = useDataAttributes(otherProps);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isFocused = _useState2[0],
      setIsFocused = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      isOpened = _useState4[0],
      setIsOpened = _useState4[1];

  var _useState5 = useState(function () {
    return {
      selectOptions: [],
      selectedOptions: [],
      activeOptionId: null,
      filteringTerm: ''
    };
  }),
      _useState6 = _slicedToArray(_useState5, 2),
      _useState6$ = _useState6[0],
      selectOptions = _useState6$.selectOptions,
      selectedOptions = _useState6$.selectedOptions,
      activeOptionId = _useState6$.activeOptionId,
      filteringTerm = _useState6$.filteringTerm,
      setSelectInfo = _useState6[1];

  var selectTriggerRef = useRef();
  var selectListRef = useRef();
  var selectInputRef = useRef();
  var id = useId(propId);

  var providerState = _objectSpread(_objectSpread({}, otherProps), {}, {
    id: id,
    selectTriggerRef: selectTriggerRef,
    selectListRef: selectListRef,
    selectInputRef: selectInputRef,
    isFocused: isFocused,
    setIsFocused: setIsFocused,
    isOpened: isOpened,
    setIsOpened: setIsOpened,
    selectOptions: selectOptions,
    selectedOptions: selectedOptions,
    activeOptionId: activeOptionId,
    filteringTerm: filteringTerm,
    setSelectInfo: setSelectInfo
  });

  return /*#__PURE__*/jsx(SelectPropsContext.Provider, {
    value: providerState,
    children: /*#__PURE__*/jsx(Select, _objectSpread(_objectSpread(_objectSpread({
      ref: ref,
      className: className
    }, ariaAttributes), dataAttributes), {}, {
      children: children
    }))
  });
});
SelectProvider$1.COMPONENT_ID = 'Gearz.Select';
SelectProvider$1.displayName = SelectProvider$1.COMPONENT_ID;
SelectProvider$1.List = SelectList;
SelectProvider$1.OptionGroup = SelectOptionGroup;
SelectProvider$1.Option = SelectOption;
SelectProvider$1.Separator = SelectOptionSeparator;
SelectProvider$1.propTypes = {
  // eslint-disable-next-line max-len
  autoFocus: PropTypes.boolWithDescription('Flag to focus the select on page load. The autoFocus prop should not be used, as [it can reduce usability and accessibility for users](https://github.com/jsx-eslint/eslint-plugin-jsx-a11y/blob/566011b89df811b95bc4e99799a5ea03d440411d/docs/rules/no-autofocus.md).'),
  className: PropTypes.stringWithDescription('Custom class to be applied.'),
  children: PropTypes.nodeWithDescription('Content of the select.'),
  name: PropTypes.stringWithDescription('Name of the select.'),
  id: PropTypes.stringWithDescription('Id of the select.'),
  appendTo: PropTypes.oneOfTypeWithDescription([PropTypes.string, PropTypes.instanceOf(getSafeHTMLElement())], 'Destination element to append the select.'),
  direction: PropTypes.oneOfWithDescription(Object.values(DIRECTIONS), 'Direction of the select list.'),
  multiple: PropTypes.boolWithDescription('Flag to make the select multi select.'),
  clearable: PropTypes.boolWithDescription('Flag to make the select clearable.'),
  searchable: PropTypes.boolWithDescription('Flag to make the select searchable.'),
  searchBy: PropTypes.oneOfTypeWithDescription([PropTypes.string, PropTypes.arrayOf(PropTypes.string)], 'Terms or terms to use in searchable select to search though the options. You can use `searchTerm`, `value` or a custom data attribute defined in `Select.Option`.'),
  searchMatcher: PropTypes.funcWithDescription('Custom function to match the option with the search term. `({ filterTerm, searchTerm, searchBy, value, dataAttributes }) => Boolean`'),
  hideSelected: PropTypes.boolWithDescription('Flag to hide the selected items of the list.'),
  placeholder: PropTypes.oneOfTypeWithDescription([PropTypes.string, PropTypes.func], 'Placeholder of the select. In case of function. `({ selectedOptions }) => ReactNode`'),
  closeOnSelect: PropTypes.boolWithDescription('Flag to close the list when a item is selected. In case of `multiple="true"`, this flag is set to `false`.'),
  invalid: PropTypes.boolWithDescription('Flag to make the select invalid.'),
  disabled: PropTypes.boolWithDescription('Flag to make the select disable.'),
  disableBackgroundScroll: PropTypes.boolWithDescription('Flag to disable the background scroll.'),
  avoidReposition: PropTypes.boolWithDescription('Flag to avoid the reposition behaviour.'),
  avoidHideOptionsOnSearch: PropTypes.boolWithDescription('Flag to avoid hide the options when they don\'t match with the search term.'),
  forceOpenEvenNotFit: PropTypes.boolWithDescription('Flag to force open the select even it doesn\'t fit in the container.'),
  forceDefaultCloseMethod: PropTypes.boolWithDescription('Flag to force the close behaviour when the select doesn\'t fit in the container.'),
  defaultValue: PropTypes.oneOfTypeWithDescription([PropTypes.number, PropTypes.string, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string]))], 'Default value of the select.'),
  value: PropTypes.oneOfTypeWithDescription([PropTypes.number, PropTypes.string, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string]))], 'Value selected of the select. (controlled)'),
  prefix: PropTypes.oneOfTypeWithDescription([PropTypes.node, PropTypes.func], 'Element to be rendered at the beginning of the select trigger. In case of function, `({ selectedOptions, disabled, multiple, invalid, isOpened, isFocused, searchable }) => ReactNode`.'),
  messageOnNoResults: PropTypes.oneOfTypeWithDescription([PropTypes.node, PropTypes.func], 'Message to render on no results in searchable select. In case of function, `({ selectOptions }) => ReactNode`.'),
  arrowRenderer: PropTypes.funcWithDescription('Custom function to render your own arrow. `({ selectedOptions, disabled, multiple, invalid, isOpened, isFocused, searchable }) => ReactNode`'),
  valueViewerFormatter: PropTypes.funcWithDescription('Custom formatter for value viewer. `({ selectedOptions, isSelectDisabled, isSelectMultiple, deselectOption: Function }) => ReactNode`'),
  onChange: PropTypes.funcWithDescription('Callback called when a new options is selected/deselected. `({ value, optionSelected, optionDeselected }) => {}`'),
  onFocus: PropTypes.funcWithDescription('Callback called when focus event is triggered.'),
  onBlur: PropTypes.funcWithDescription('Callback called when blur event is triggered.'),
  onOpened: PropTypes.funcWithDescription('Callback called when select is opened.'),
  onClosed: PropTypes.funcWithDescription('Callback called when select is closed.')
};
SelectProvider$1.defaultProps = {
  autoFocus: false,
  className: '',
  children: null,
  name: null,
  id: null,
  appendTo: null,
  direction: DIRECTIONS.DOWN,
  disableBackgroundScroll: false,
  avoidReposition: false,
  avoidHideOptionsOnSearch: false,
  forceOpenEvenNotFit: false,
  forceDefaultCloseMethod: false,
  multiple: false,
  clearable: undefined,
  searchable: false,
  hideSelected: false,
  searchBy: ['searchTerm', 'value'],
  placeholder: null,
  closeOnSelect: null,
  invalid: false,
  disabled: false,
  defaultValue: null,
  value: null,
  prefix: undefined,
  messageOnNoResults: 'No results found.',
  arrowRenderer: null,
  searchMatcher: DEFAULT_SEARCH_MATCHER,
  valueViewerFormatter: DEFAULT_VALUE_VIEWER_FORMATTER,
  onChange: noop,
  onFocus: noop,
  onBlur: noop,
  onOpened: noop,
  onClosed: noop
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var SelectProvider = styled(SelectProvider$1).withConfig({
  componentId: "gz-select-1675939618424__sc-10kfhzu-0"
})(["", ""], function (props) {
  return getThemeByProps({
    componentId: SelectProvider$1.COMPONENT_ID,
    props: props
  });
});
SelectProvider.displayName = SelectProvider$1.displayName;
SelectProvider.propTypes = SelectProvider$1.propTypes;
SelectProvider.defaultProps = SelectProvider$1.defaultProps;

export { SelectProvider as S, SelectOption as a, SelectOptionGroup as b };
